// istanbul ignore file

import { useUserQuery } from "~/api/user";
import { CurrentStepProps } from "../../getSideMenuItems";
import { useAddProductTag, useProductTagsQuery } from "~/api/productTags";
import { StrapiProductTag } from "~/shared-types";
import { Select } from "~/components/Backroom/Select";
import { useEffect } from "react";
import InfoModal from "~/components/Backroom/Infomodal";
import { useWithFetchAll } from "~/api/useWithFetchAll";
import Image from "next/image";

export const ProductTagsSelect = ({ form, product }: CurrentStepProps) => {
  const { data: user } = useUserQuery();
  const { data, fetchAllPages, isFetching } = useWithFetchAll(
    useProductTagsQuery({
      filters: { store: user?.store.id },
    })
  );
  const addProductTag = useAddProductTag({
    filters: { store: user?.store.id },
  });
  const { control, setValue, getValues } = form;
  const productTags = data?.pages
    ?.flatMap((page) => page?.data)
    .filter(Boolean) as StrapiProductTag[];

  const options = productTags?.map((tag) => ({
    label: tag.attributes.title,
    value: tag.id,
  }));

  // Effect: Fetch all product tags immediately
  useEffect(() => {
    fetchAllPages();
  }, []);

  const onCreateOption = async (inputValue: string) => {
    if (!product?.id) return;

    const newProductTag = await addProductTag.mutateAsync({
      title: inputValue,
      productId: product?.id,
    });

    const currentValues = getValues("productTags");
    setValue(
      "productTags",
      [
        ...(currentValues ?? []),
        { label: inputValue, value: newProductTag.id },
      ],
      { shouldDirty: true }
    );
  };

  return (
    <Select
      control={control}
      id="productTags"
      className="mb-12"
      label={
        <InfoModal id="productTagsDescription" label="Tilføj butikskategorier:">
          <div className="flex flex-col gap-4">
            <p>
              Butikskategorier fremgår af butikkens profilside og kan bruges til
              at filtrere produktoversigten. Disse kategorier er unikke for din
              butik og vil kunne vælges på tværs af dine produkter. Brug f.eks.
              butikskategorier til at fremhæve materialer, kollektioner eller
              lignende.
            </p>
            <Image
              className="mx-auto mt-4"
              src="/assets/images/product-tags.svg"
              alt="Illustration af butikskategorier"
              width="340"
              height="422"
            />
            <p className="mt-4">
              Du kan tilføje nye kategorier ved at skrive dem i feltet og trykke
              "Tilføj". Du kan også se, redigere og slette butikskategorier fra
              din profil.
            </p>
            <Image
              className="mx-auto mt-4"
              src="/assets/images/product-tags-link.svg"
              alt="Illustration af link til butikskategorier"
              width="354"
              height="309"
            />
          </div>
        </InfoModal>
      }
      aria-describedby="productTagsDescription"
      isLoading={isFetching}
      options={options}
      creatable
      isMulti
      onCreateOption={onCreateOption}
      noOptionsMessage={() =>
        "Begynd at skrive for at tilføje en butikskategori"
      }
    />
  );
};
